import { useMemo } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Link from "@mui/material/Link";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Divider from "@mui/material/Divider";
import { parseMarkdown } from "../utils/markdown";

// Renders a help page from the parse tree as React elements. Nothing is ever
// injected as HTML, so a page cannot carry script or markup into the app.

const HEADING_VARIANT = { 1: "h5", 2: "h5", 3: "h6", 4: "subtitle1", 5: "subtitle2", 6: "subtitle2" };

function Inline({ nodes }) {
  return (nodes || []).map((n, i) => {
    switch (n.type) {
      case "text":
        return n.text;
      case "br":
        return <br key={i} />;
      case "strong":
        return <strong key={i}><Inline nodes={n.children} /></strong>;
      case "em":
        return <em key={i}><Inline nodes={n.children} /></em>;
      case "code":
        return (
          <Box
            key={i}
            component="code"
            sx={{ fontFamily: "monospace", fontSize: "0.9em", bgcolor: "action.hover", px: 0.5, borderRadius: 0.5 }}
          >
            {n.text}
          </Box>
        );
      case "link":
        return n.href ? (
          <Link key={i} href={n.href} target="_blank" rel="noopener noreferrer">
            <Inline nodes={n.children} />
          </Link>
        ) : (
          <Inline key={i} nodes={n.children} />
        );
      default:
        return null;
    }
  });
}

function Blocks({ blocks, tight = false }) {
  return blocks.map((b, i) => <Block key={i} block={b} tight={tight} />);
}

function Block({ block, tight }) {
  switch (block.type) {
    case "heading":
      return (
        <Typography
          variant={HEADING_VARIANT[block.level]}
          component={`h${Math.min(block.level + 1, 6)}`}
          sx={{ mt: block.level <= 2 ? 3 : 2, mb: 1, fontWeight: 600 }}
        >
          <Inline nodes={block.children} />
        </Typography>
      );
    case "paragraph":
      return (
        <Typography variant="body2" component="p" sx={{ mb: tight ? 0.5 : 1.5, lineHeight: 1.6 }}>
          <Inline nodes={block.children} />
        </Typography>
      );
    case "quote":
      return (
        <Box
          sx={{
            borderLeft: 4,
            borderColor: "primary.light",
            bgcolor: "action.hover",
            borderRadius: 1,
            px: 2,
            pt: 1.5,
            pb: 0.5,
            mb: 1.5,
          }}
        >
          <Blocks blocks={block.children} />
        </Box>
      );
    case "list": {
      const Tag = block.ordered ? "ol" : "ul";
      return (
        <Box component={Tag} start={block.start} sx={{ pl: 3, mt: 0, mb: tight ? 0.5 : 1.5 }}>
          {block.items.map((item, i) => (
            <Typography key={i} component="li" variant="body2" sx={{ mb: 0.5, lineHeight: 1.6 }}>
              <Blocks blocks={item} tight />
            </Typography>
          ))}
        </Box>
      );
    }
    case "table":
      return (
        <TableContainer sx={{ mb: 2, border: 1, borderColor: "divider", borderRadius: 1 }}>
          <Table size="small">
            {block.header.some((c) => c.length) && (
              <TableHead>
                <TableRow>
                  {block.header.map((c, k) => (
                    <TableCell key={k} align={block.align[k]} sx={{ fontWeight: 600, verticalAlign: "bottom" }}>
                      <Inline nodes={c} />
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
            )}
            <TableBody>
              {block.rows.map((row, r) => (
                <TableRow key={r}>
                  {row.map((c, k) => (
                    <TableCell key={k} align={block.align[k]} sx={{ verticalAlign: "top" }}>
                      <Inline nodes={c} />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      );
    case "code":
      return (
        <Box
          component="pre"
          sx={{
            fontFamily: "monospace",
            fontSize: "0.85rem",
            bgcolor: "action.hover",
            p: 1.5,
            borderRadius: 1,
            overflowX: "auto",
            mb: 1.5,
          }}
        >
          {block.text}
        </Box>
      );
    case "rule":
      return <Divider sx={{ my: 2 }} />;
    default:
      return null;
  }
}

export default function MarkdownView({ text }) {
  const blocks = useMemo(() => parseMarkdown(text), [text]);
  return (
    <Box sx={{ "& li > p:last-child": { mb: 0 }, overflowWrap: "anywhere" }}>
      <Blocks blocks={blocks} />
    </Box>
  );
}
